import React from "react";
import ConfigProvider from "./common/ConfigProvider";
import PropTypes from "prop-types";
import useBackboneModel from "./helper/useBackboneModel";
import { useEffect, useState } from 'react';
import ti18n from 'i18n!nls/Sequenceri18n';
import SequencerSearchWidget from "../models/audioEditor/SequencerSearchWidget";
import useDefaultTrebbleAntDTokenConfig from "./helper/useDefaultTrebbleAntDTokenConfig";
import SequencerEditBoundariesClipDownloadUtils from "../models/audioEditor/SequencerEditBoundariesClipDownloadUtils";
import SearchSettingsContextMenu from "./SearchSettingsContextMenu";
import Tooltip from "./common/Tooltip";

// Import the new smaller components
import EditorSearchAnalysisState from "./EditorSearchAnalysisState";
import EditorSearchHeader from "./EditorSearchHeader";
import EditorSearchResults from "./EditorSearchResults";
import EditorSearchParams from "./EditorSearchParams";

const SEARCH_TYPE = SequencerSearchWidget.SEARCH_TYPE;

const EditorSearchContextPanel = (props) => {
    const themeConfig = useDefaultTrebbleAntDTokenConfig({useEditorTheme: true});
    
    // Backbone model integration
    const searchWidgetData = useBackboneModel(props.searchWidgetModel, (model) => {
        model.toJSON()
        return {
            searchRequestId: model.getSearchRequestId(),
            searchResultIndexInFocus: model.getIndexOfNodeInFocus(),
            numberOfSearchNodesFound: model.getNumberOfSearchNodesFound(),
            arrayOfSequencerNodeInFocus : model.getSequencerNodesCurrentlyInFocus(),
            allArraysOfNodesMatchingResults: model.getAllArraysOfNodesMatchingResults(),
            allArraysOfNodesToHiglights: model.getAllArraysOfNodesToHiglights(),
            allArraysOfAdditionalMatchingResults: model.getAllArraysOfAdditionalMatchingResults(),
            allfilterProperties: model.getAllfilterProperties(),
            filterOptions: model.getFilterOptions(),
            canMoveToPreviousResult:model.canMoveToPreviousResult(),
            canMoveToNextResult:model.canMoveToNextResult(),
            searchType: model.getSearchType(),
            searchParams: model.getSearchParams(),
            onShowSafeCut: model.isOnlyShowSafeCut(),
            shouldSafeBeAvailable: model.shouldSafeBeAvailableForCurrentSearchType(),
            isMediaAnalyzedForSilences: model.isMediaAnalyzedForSilences && model.isMediaAnalyzedForSilences(),
            mediaAnalysisProgress: model.getMediaAnalysisProgress?.() ?? 0,
            mediaAnalysisReason: model.getMediaAnalysisReason?.(),
            autoJumpToNext: model.getAutoJumpToNext?.() ?? false,
            previewWithOperation: model.getPreviewWithOperation?.() ?? false,
            previewWithoutOperation: model.getPreviewWithoutOperation?.() ?? false
        }
    });

    // Local state
    const [timerForInputChange, setTimerForInputChange] = useState(null);
    const [hasResults, setHasResults] = useState(searchWidgetData.allArraysOfNodesMatchingResults && searchWidgetData.allArraysOfNodesMatchingResults.length > 0);
    const [displayRefreshButton, setDisplayRefreshButton] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchPanelTitle, setSearchPanelTitle] = useState(window.getI18n(ti18n,"SEARCH")?.toUpperCase());
    const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);

    // Update hasResults when search data changes
    useEffect(() => {
        setHasResults(searchWidgetData.allArraysOfNodesMatchingResults && searchWidgetData.allArraysOfNodesMatchingResults.length > 0);
    }, [searchWidgetData.allArraysOfNodesMatchingResults]);

    // Update refresh button display based on search type
    useEffect(() => {
        switch(searchWidgetData.searchType){
            case SEARCH_TYPE.TEXT_SEARCH:
            case SEARCH_TYPE.SEMANTIC_SEARCH:
            case SEARCH_TYPE.FILLER_WORDS:
            case SEARCH_TYPE.EDIT_BOUNDARIES:
            case SEARCH_TYPE.SILENCES:
            case SEARCH_TYPE.DISFLUENCY_SEARCH:
                setDisplayRefreshButton(false);
                break;
            case SEARCH_TYPE.AI_EDIT_SUGGESTIONS:
                setDisplayRefreshButton(true);
                break;
        }
    }, [searchWidgetData.searchType]);

    useEffect(() => {
        if(searchWidgetData.searchType == SEARCH_TYPE.SILENCES){
            setSearchPanelTitle(window.getI18n(ti18n,"SILENCE_FINDER"));
            return;
        }else{
            setSearchPanelTitle(window.getI18n(ti18n,"SEARCH"));
            return;
        }
    }, [searchWidgetData.searchType]);

    // Event handlers
    const onInputValueChange = async (e) => {
        if(searchWidgetData.searchType === SEARCH_TYPE.SEMANTIC_SEARCH){
            if(e.target.value.length > 5){
                if (timerForInputChange) {
                    clearTimeout(timerForInputChange);
                }
                const timeoutId = setTimeout(async ()=>{
                    await props.searchWidgetModel.applySemanticTextSearch(e.target.value);
                }, 3000);
                setTimerForInputChange(timeoutId);
            }
        }else{
           await props.searchWidgetModel.applyTextSearch(e.target.value);
        }        
    }

    const onApplyAllButtonClick = async() => {
        if(searchWidgetData.searchType == SEARCH_TYPE.EDIT_BOUNDARIES){
            return await SequencerEditBoundariesClipDownloadUtils.getInstance().downloadAllClipWithEditBoundaries(props.sequencer, props.searchWidgetModel);
        }
        if(props.onApplyAllButtonClick){
            props.onApplyAllButtonClick();
            if(window.trebbleAnalyticsHelper){
                window.trebbleAnalyticsHelper.trackEvent("sequencer", 'searchResultApplyActionOnAllResults', 'search result apply action on all results',{  searchParams: searchWidgetData.searchParams, searchType: searchWidgetData.searchType, searchRequestId: searchWidgetData.searchRequestId});
            }
        }else{
            props.sequencer.deleteAllSearchResults();
            props.onPanelCloseButtonClick();
            if(window.trebbleAnalyticsHelper){
                window.trebbleAnalyticsHelper.trackEvent("sequencer", 'searchResultDeleteAllResults', 'search result delete all results',{  searchParams: searchWidgetData.searchParams, searchType: searchWidgetData.searchType, searchRequestId: searchWidgetData.searchRequestId});
            }
        }
    }

    const onRefreshButtonClick = async() => {
        if(searchWidgetData.searchType == SEARCH_TYPE.AI_EDIT_SUGGESTIONS){
            setIsRefreshing(true);
            try {
                const isEditLikeMeMode = searchWidgetData.searchParams?.isEditLikeMeMode === true || searchWidgetData.searchParams?.isEditLikeMeMode === "true";
                if (isEditLikeMeMode) {
                    await props.sequencer.displayAIEditSuggestionsInEditLikeMeMode(true);
                } else {
                    await props.sequencer.getSearchWidget().applyAIEditSuggestions(searchWidgetData.searchParams, true, false);
                }
                if(window.trebbleAnalyticsHelper){
                    window.trebbleAnalyticsHelper.trackEvent("sequencer", 'searchResultManualRefresh', 'search result manual refresh',{  searchParams: searchWidgetData.searchParams, searchType: searchWidgetData.searchType, searchRequestId: searchWidgetData.searchRequestId});
                }
            } finally {
                setIsRefreshing(false);
            }
        }
    }

    const onSafeCutToggle = (isChecked) => {
        props.searchWidgetModel.setOnShowSafeCut(isChecked);
        if(window.trebbleAnalyticsHelper){
            window.trebbleAnalyticsHelper.trackEvent("sequencer", 'safeCutToggle', 'safe cut toggle', {
                safeCutEnabled: isChecked,
                searchParams: searchWidgetData.searchParams,
                searchType: searchWidgetData.searchType,
                searchRequestId: searchWidgetData.searchRequestId
            });
        }
    }

    const onFilterChange = (values) => {
        props.searchWidgetModel.updateFilterOptions(values);
    };

    const onSearchParamsChange = (newParams) => {
        // Handle search parameter changes
        if(window.trebbleAnalyticsHelper){
            window.trebbleAnalyticsHelper.trackEvent("sequencer", 'searchParamsChanged', 'search parameters changed', {
                searchParams: newParams,
                searchType: searchWidgetData.searchType,
                searchRequestId: searchWidgetData.searchRequestId
            });
        }
    };

    // Computed values
    const applyAllButtonLabel = props.applyAllActionButtonLabel || ( 
        searchWidgetData.searchType == SEARCH_TYPE.EDIT_BOUNDARIES 
            ? window.getI18n(ti18n, "DOWNLOAD_ALL")
            : window.getI18n(ti18n, "REMOVE_ALL")
    );

    return (
        <ConfigProvider theme={themeConfig}>
            <div className="flex flex-col box-border p-2 search_context_panel">
                {/* Panel Title */}
                <div className="flex flex-row items-center justify-between">
                    <b className="flex flex-col">
                        {props.title || searchPanelTitle}
                    </b>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center">
                        {/* Settings Button */}
                        <SearchSettingsContextMenu
                            useEditorTheme={true}
                            searchWidgetModel={props.searchWidgetModel}
                            open={settingsDropdownOpen}
                            onOpenChange={setSettingsDropdownOpen}
                        >
                            <Tooltip title={window.getI18n(ti18n, "SEARCH_SETTINGS")} placement="bottom" align="center">
                                <button aria-describedby="search-settings-menu">
                                    <span className="icon pe-7s-edit text-2xl"></span>
                                </button>
                            </Tooltip>
                        </SearchSettingsContextMenu>
                        
                        {/* Close Button */}
                        <Tooltip title={window.getI18n(ti18n, "CLOSE")} placement="bottom" align="center">
                            <button onClick={props.onPanelCloseButtonClick}>
                                <span className="icon pe-7s-close text-3xl"></span>
                            </button>
                        </Tooltip>
                    </div>
                </div>

                {/* Search Header - Input field, search type display, refresh button */}
                <EditorSearchHeader
                    searchType={searchWidgetData.searchType}
                    searchParams={searchWidgetData.searchParams}
                    displayRefreshButton={displayRefreshButton}
                    isRefreshing={isRefreshing}
                    onInputValueChange={onInputValueChange}
                    onRefreshButtonClick={onRefreshButtonClick}
                    panelOpened={props.panelOpened}
                />

                {/* Search Parameters Configuration */}
                <EditorSearchParams
                    searchType={searchWidgetData.searchType}
                    searchParams={searchWidgetData.searchParams}
                    onSearchParamsChange={onSearchParamsChange}
                    searchWidgetModel={props.searchWidgetModel}
                    isAnalyzing={searchWidgetData.isMediaAnalyzedForSilences}
                />

                {/* Search Display Title */}
                <b className="py-2 text-left">{props.searchDisplayTitle}</b>

                {/* Analysis State or Results */}
                {searchWidgetData.isMediaAnalyzedForSilences ? (
                    <EditorSearchAnalysisState
                        isMediaAnalyzedForSilences={searchWidgetData.isMediaAnalyzedForSilences}
                        shouldSafeBeAvailable={searchWidgetData.shouldSafeBeAvailable}
                        mediaAnalysisProgress={searchWidgetData.mediaAnalysisProgress}
                        searchType={searchWidgetData.searchType}
                        mediaAnalysisReason={searchWidgetData.mediaAnalysisReason}
                    />
                ) : (
                    <EditorSearchResults
                        searchWidgetData={searchWidgetData}
                        hasResults={hasResults}
                        applyAllButtonLabel={applyAllButtonLabel}
                        onApplyAllButtonClick={onApplyAllButtonClick}
                        onSafeCutToggle={onSafeCutToggle}
                        onFilterChange={onFilterChange}
                        searchWidgetModel={props.searchWidgetModel}
                    />
                )}
            </div>
        </ConfigProvider>
    );
};

EditorSearchContextPanel.propTypes = {
    title: PropTypes.string,
    showSearchInput: PropTypes.bool,
    searchDisplayTitle: PropTypes.string.isRequired,
    searchWidgetModel: PropTypes.object,
    onPanelCloseButtonClick: PropTypes.func,
    resfreshButtonClick: PropTypes.func,
    panelOpened: PropTypes.bool,
    resultCursor: PropTypes.number,
    totalNumberOfResults: PropTypes.number,
    applyActionButtonLabel: PropTypes.string,
    resfreshButtonLabel: PropTypes.string,
    applyAllActionButtonLabel : PropTypes.string,
    onApplyButtonClick: PropTypes.func,
    onApplyAllButtonClick: PropTypes.func,
    locale: PropTypes.object,
    sequencer:PropTypes.object
};

export default EditorSearchContextPanel;
